import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import { Button } from "@/components/ui/button";
import { api, saveAuth } from "@/lib/api";
import { toast } from "sonner";

const OTP_LEN = 6;
const EXPIRY_SEC = 10 * 60;
const RESEND_COOLDOWN = 30;

export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const [digits, setDigits] = useState(Array(OTP_LEN).fill(""));
  const [remaining, setRemaining] = useState(EXPIRY_SEC);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);
  const nav = useNavigate();

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const onChange = (i, v) => {
    v = v.replace(/\D/g, "").slice(0, 1);
    const next = [...digits]; next[i] = v; setDigits(next);
    if (v && i < OTP_LEN - 1) inputsRef.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LEN);
    if (text.length) {
      const arr = text.split("").concat(Array(OTP_LEN).fill("")).slice(0, OTP_LEN);
      setDigits(arr);
      inputsRef.current[Math.min(text.length, OTP_LEN - 1)]?.focus();
    }
  };

  const verify = async () => {
    const otp = digits.join("");
    if (otp.length !== OTP_LEN) { toast.error("Please enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      saveAuth(data.token, data.user);
      toast.success("Email verified — welcome!");
      nav("/candidate/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new verification code has been sent.");
      setResendIn(RESEND_COOLDOWN);
      setRemaining(EXPIRY_SEC);
      setDigits(Array(OTP_LEN).fill(""));
      inputsRef.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally { setResending(false); }
  };

  const maskedEmail = email.replace(/^(.{1})(.*)(@.*)$/, (_, a, b, c) => `${a}${"*".repeat(Math.max(1, b.length))}${c}`);
  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50" data-testid="verify-email-page">
      <div className="hidden md:flex vs-hero-gradient text-white p-12 flex-col justify-between">
        <VSLogo dark size={44} showWordmark/>
        <div>
          <h2 className="font-display text-4xl font-bold">Verify your email</h2>
          <p className="mt-4 text-slate-300 max-w-md">We've sent a 6-digit verification code to your inbox. Enter the code to activate your VS-Solutions Careers account.</p>
        </div>
        <div className="text-xs text-slate-400">© 2026 Vijay Software Solutions Pvt. Ltd.</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">Verify Your Email</h1>
          <p className="text-slate-500 mt-2">We sent a verification code to <span className="font-medium text-[#0A2540]">{maskedEmail || email}</span></p>

          <div className="mt-8 flex justify-between gap-2" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => onChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                className="w-12 h-14 rounded-lg border border-slate-300 text-center text-xl font-bold text-[#0A2540] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/20 outline-none"
                data-testid={`otp-input-${i}`}
              />
            ))}
          </div>

          <p className="mt-3 text-sm text-slate-500" data-testid="otp-expiry">
            {remaining > 0 ? <>Code expires in <span className="font-medium text-[#0A2540]">{fmt(remaining)}</span></> : "Code has expired. Please request a new one."}
          </p>

          <Button onClick={verify} disabled={loading || digits.join("").length !== OTP_LEN}
            className="mt-6 w-full bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="verify-otp-submit">
            {loading ? "Verifying..." : "Verify Email"}
          </Button>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button onClick={resend} disabled={resendIn > 0 || resending}
              className={`font-medium ${resendIn > 0 ? "text-slate-400 cursor-not-allowed" : "text-[#2563EB] hover:underline"}`}
              data-testid="resend-otp-btn">
              {resending ? "Sending..." : resendIn > 0 ? `Resend in ${resendIn}s` : "Resend Code"}
            </button>
            <Link to="/register" className="text-slate-500 hover:text-slate-700" data-testid="change-email-link">Change email</Link>
          </div>

          <p className="mt-10 text-xs text-slate-400 leading-relaxed">
            Didn't receive the email? Check your spam folder. VS-Solutions Careers will never ask for your password by email.
          </p>
        </div>
      </div>
    </div>
  );
}
